import type {ReactElement} from 'react';
import React from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {getAssetUrl} from '@Utils/s3.util';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './download-panel-footer.module.scss';
import {isUserPremium} from '@Libraries/user.library';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {useAppDispatch} from '@/hooks';
import {MODALS_IDS} from '@Components/modal-container';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

enum MoreOptions {
  DOWNLOAD_TO_DRIVE = 'drive',
  DOWNLOAD_TO_DROPBOX = 'dropbox',
  RESET_TO_FREE_SETTINGS = 'reset_to_free_settings',
}
interface DownloadPanelFooterProps {
  className?: string;
  onClickDownload: () => void;
  onDownloadToDrive: () => Promise<void>;
  onDownloadToDropbox: () => Promise<void>;
  hasTopShadow?: boolean;
  showLoading?: boolean;
}

export function DownloadPanelFooter({className = '', showLoading = false, hasTopShadow = true, ...props}: DownloadPanelFooterProps): ReactElement {
  const dispatch = useAppDispatch();
  const trackGAEvent = (id: MoreOptions): void => {
    trackPosterBuilderGA4Events(GA4EventName.SAVE_TO_DRIVE, {
      [GA4EventParamName.TYPE]: id,
    });
  };

  const onClick = (id: MoreOptions): void => {
    switch (id) {
      case MoreOptions.DOWNLOAD_TO_DRIVE:
        trackGAEvent(id);
        void props.onDownloadToDrive();
        break;

      case MoreOptions.DOWNLOAD_TO_DROPBOX:
        trackGAEvent(id);
        void props.onDownloadToDropbox();
        break;
    }

    if (isEditorMobileVariant()) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const onClickDownload = async (): Promise<void> => {
    props.onClickDownload();
  };

  const getPopup = (): ReactElement => {
    return (
      <>
        <ClickableDiv
          id={MoreOptions.DOWNLOAD_TO_DRIVE}
          className={`flex-row-align-center spacing-p-3 hover-transition-all spacing-m-b-2 radius-4 ${styles.socialMediaOption}`}
          onClick={(): void => {
            onClick(MoreOptions.DOWNLOAD_TO_DRIVE);
          }}
        >
          <img className={styles.socialThumbs} src={getAssetUrl('google-drive-logo.png')} alt={window.i18next.t('pmwjs_save_to_google_drive')} />
          <Text val={window.i18next.t('pmwjs_save_to_google_drive')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        <ClickableDiv
          id={MoreOptions.DOWNLOAD_TO_DROPBOX}
          className={`flex-row-align-center spacing-p-3 hover-transition-all ${isUserPremium() ? '' : 'spacing-m-b-2'} radius-4 ${styles.socialMediaOption}`}
          onClick={(): void => {
            onClick(MoreOptions.DOWNLOAD_TO_DROPBOX);
          }}
        >
          <img className={styles.socialThumbs} src={getAssetUrl('dropbox-logo.png')} alt={window.i18next.t('pmwjs_save_to_dropbox')} />
          <Text val={window.i18next.t('pmwjs_save_to_dropbox')} className="content-body" size={TextSize.XSMALL} />
        </ClickableDiv>
        {isUserPremium() ? null : (
          <ClickableDiv
            id={MoreOptions.RESET_TO_FREE_SETTINGS}
            className={`flex-row-align-center hover-transition-all spacing-p-3 radius-4 ${styles.socialMediaOption}`}
            onClick={(): void => {
              onClick(MoreOptions.RESET_TO_FREE_SETTINGS);
            }}
          >
            <Icon icon="icon-undo" className="content-body" size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_reset_to_free_settings')} className="content-body" size={TextSize.XSMALL} />
          </ClickableDiv>
        )}
      </>
    );
  };

  const getDownloadButtonText = (): string => {
    // return window.i18next.t('pmwjs_download_x_price', {
    //   price: '$4.49',
    // });
    return window.i18next.t('pmwjs_download');
  };

  const getPanelFooter = (): ReactElement => {
    return (
      <div className={`spacing-p-4 flex-v-row ${styles.footerContainer} ${className} ${hasTopShadow ? styles.topBorder : ''}`}>
        <div className="flex-h-row flex-items-center">
          <Button
            isLoading={showLoading}
            customClasses="_full-width"
            type={Type.PRIMARY}
            size={Size.SMALL}
            icon="icon-download"
            onClick={(): void => {
              void onClickDownload();
            }}
            text={getDownloadButtonText()}
          />
          <Dropdown
            popupClassName={styles.popupClasses}
            position={DROPDOWN_POSITION.FIXED}
            alignment={ALIGNMENT_TYPE.BOTTOM_END}
            selector={<Icon className={`spacing-m-l-2 ${styles.footerIcon}`} icon="icon-menu-dots" type={IconType.PRIMARY} shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_20} />}
            popup={getPopup()}
            popUpHasCustomWidth
            popupSpacingClass="spacing-p-3"
            showModalForSmallScreen
            modalTitle={window.i18next.t('pmwjs_download_options')}
          />
        </div>
      </div>
    );
  };

  return getPanelFooter();
}
